import { client, url } from './pocketbase.js'
import tenant from '@/store/tenant.js'
import { applySosModeFromTenantRecord } from '@/store/sosMode.js'
import { get } from 'svelte/store'

const TENANT_ROOT_DOMAIN = process.env.TENANT_ROOT_DOMAIN
const MAIN_DOMAINS = ['localhost', '127.0.0.1', 'lvh.me', TENANT_ROOT_DOMAIN, `www.${TENANT_ROOT_DOMAIN}`]

export const tenantRootDomain = TENANT_ROOT_DOMAIN

function customDomainLookupHostnames(hostname) {
    const h = String(hostname || '').toLowerCase()
    if (!h) return []
    const list = [h]
    if (h.startsWith('www.')) {
        list.push(h.slice(4))
    } else {
        list.push('www.' + h)
    }
    return [...new Set(list)]
}

export function getTenantSlug() {
    const hostname = window.location.hostname
    
    if (MAIN_DOMAINS.some(domain => hostname === domain || hostname.endsWith('.' + domain))) {
        const parts = hostname.split('.')
        
        if (hostname.endsWith('.localhost') || hostname.endsWith('.lvh.me')) {
            return parts[0] !== 'www' ? parts[0] : null
        }
        
        if (hostname.endsWith(`.${TENANT_ROOT_DOMAIN}`)) {
            const subdomain = parts[0]
            if (subdomain !== 'www' && subdomain !== 'app') {
                return subdomain
            }
        }
        
        return null
    }
    
    return null
}

function applyTenantFromRecord(result) {
    tenant.commit('id', result.id)
    tenant.commit('name', result.name)
    tenant.commit('slug', result.slug)
    tenant.commit('logo', result.logo ? `${url}api/files/${result.collectionId}/${result.id}/${result.logo}` : '')
    tenant.commit('primaryColor', result.primaryColor || '#4CAF50')
    tenant.commit('plan', result.plan || 'free')
    tenant.commit('subscriptionStatus', result.subscription_status || 'none')
    tenant.commit('customDomain', result.custom_domain || '')
    tenant.commit('line', result.line || '')
    tenant.commit('whatsapp', result.whatsapp || '')
    tenant.commit('instagram', result.instagram || '')
    tenant.commit('address', result.address || '')
    tenant.commit('addressLink', result.address_link || '')
    tenant.commit('loaded', true)
    applySosModeFromTenantRecord(result)
    applyTenantTheme(result.primaryColor)
}

export async function loadTenant(slug) {
    if (!slug) {
        tenant.commit('loaded', true)
        return null
    }
    try {
        const result = await client.collection('tenants').getFirstListItem(`slug = "${slug}"`)
        applyTenantFromRecord(result)
        return result
    } catch (error) {
        console.error('Failed to load tenant:', error)
        tenant.commit('loaded', true)
        return null
    }
}

export async function loadTenantById(id) {
    if (!id) {
        tenant.commit('loaded', true)
        return null
    }
    try {
        const result = await client.collection('tenants').getOne(id)
        applyTenantFromRecord(result)
        return result
    } catch (error) {
        console.error('Failed to load tenant by id:', error)
        tenant.commit('loaded', true)
        return null
    }
}

export async function initTenant() {
    const slug = getTenantSlug()
    if (slug) {
        const result = await loadTenant(slug)
        if (!result) {
            console.warn('Tenant not found for slug:', slug)
        }
        return result
    }
    const hostname = window.location.hostname
    if (!MAIN_DOMAINS.includes(hostname)) {
        for (const h of customDomainLookupHostnames(hostname)) {
            try {
                const result = await client.collection('tenants').getFirstListItem(`custom_domain = "${h}"`)
                if (result) {
                    applyTenantFromRecord(result)
                    return result
                }
            } catch (err) {
                console.error('Tenant-by-domain resolve failed:', err)
            }
        }
    }
    tenant.reset()
    tenant.commit('loaded', true)
    return null
}

export function getTenantId() {
    return get(tenant).id
}

export function isInTenantContext() {
    const slug = getTenantSlug()
    if (slug) return !!get(tenant).id
    const hostname = window.location.hostname
    if (!MAIN_DOMAINS.includes(hostname)) return !!get(tenant).id
    return false
}

export async function ensureTenantCart() {
    const tenantId = get(tenant).id
    const cached = get(tenant).posCartId
    if (cached) return cached
    if (!tenantId) return null
    try {
        const list = await client.collection('cart').getList(1, 1, { filter: `tenant = "${tenantId}"` })
        if (list.items.length > 0) {
            const id = list.items[0].id
            tenant.commit('posCartId', id)
            return id
        }
        const created = await client.collection('cart').create({ tenant: tenantId })
        if (created?.id) {
            tenant.commit('posCartId', created.id)
            return created.id
        }
    } catch (e) {
        console.error('ensureTenantCart failed:', e)
    }
    return null
}

export function isMultiTenant() {
    return get(tenant).id !== null
}

export function requiresTenant() {
    return get(tenant).id !== null
}

export async function checkSlugAvailable(slug) {
    if (!slug || slug.length < 2) return false
    
    const reserved = ['www', 'app', 'api', 'admin', 'dashboard', 'login', 'signup', 'auth']
    if (reserved.includes(slug.toLowerCase())) return false
    
    return true
}

export function generateSlug(name) {
    if (!name) return ''
    return name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 50)
}

export function applyTenantTheme(primaryColor) {
    if (!primaryColor) return
    document.documentElement.style.setProperty('--tenant-primary', primaryColor)
    document.documentElement.style.setProperty('--tenant-primary-rgb', hexToRgb(primaryColor))
}

function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (!result) return '76, 175, 80'
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
}

export function isOnSaaSRootDomain() {
    const hostname = window.location.hostname
    return MAIN_DOMAINS.includes(hostname)
}

export function getTenantRedirectUrl(slug) {
    const protocol = window.location.protocol
    const port = window.location.port ? `:${window.location.port}` : ''
    
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.endsWith('.localhost')) {
        return `${protocol}//${slug}.localhost${port}`
    }

    if (window.location.hostname.endsWith('.lvh.me') || window.location.hostname === 'lvh.me') {
        return `${protocol}//${slug}.lvh.me${port}`
    }
    
    return `${protocol}//${slug}.${TENANT_ROOT_DOMAIN}`
}

export function clearTenant() {
    tenant.reset()
}

export function getSaasRootUrl() {
    const protocol = window.location.protocol
    const port = window.location.port ? `:${window.location.port}` : ''
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.endsWith('.localhost')) {
        return `${protocol}//localhost${port}`
    }
    if (window.location.hostname.endsWith('.lvh.me') || window.location.hostname === 'lvh.me') {
        return `${protocol}//lvh.me${port}`
    }
    return `${protocol}//${TENANT_ROOT_DOMAIN}`
}

const PLAN_HIERARCHY = { free: 0, basic: 1, pro: 2 }

const PLAN_LIMITS = {
    free: { products: 50, teamMembers: 1, analytics: false, multiLocation: false, apiAccess: false },
    basic: { products: Infinity, teamMembers: 5, analytics: true, multiLocation: false, apiAccess: false },
    pro: { products: Infinity, teamMembers: Infinity, analytics: true, multiLocation: true, apiAccess: true },
}

export function getTenantPlan() {
    const t = get(tenant)
    return t.plan || 'free'
}

export function isSubscriptionActive() {
    const t = get(tenant)
    const status = t.subscriptionStatus
    return status === 'active' || status === 'trialing'
}

export function getPlanLimits(plan) {
    return PLAN_LIMITS[plan] || PLAN_LIMITS.free
}

export function canAccess(feature) {
    const plan = getTenantPlan()
    const limits = getPlanLimits(plan)
    if (feature in limits) return !!limits[feature]
    return true
}

export function getPlanLimit(feature) {
    const plan = getTenantPlan()
    const limits = getPlanLimits(plan)
    return limits[feature] !== undefined ? limits[feature] : null
}

export function requiresPlan(minimumPlan) {
    const current = getTenantPlan()
    return (PLAN_HIERARCHY[current] || 0) >= (PLAN_HIERARCHY[minimumPlan] || 0)
}
